import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Spinner from '../../Spinner';
import {
  loadTeamSeatMembers,
  peekTeamSeatMembers,
  TeamSeatMembersState,
} from '../../../utils/admin/teamSeatMembersCache';
import { TeamUserSubscriptionItem } from '../../../types/admin/subscriptionTracker';
import { formatLastLogin } from '../../../utils/admin/formatSubscriptionDate';

interface SeatMembersHoverProps {
  teamId: number;
  enabled?: boolean;
  children: React.ReactNode;
  className?: string;
}

function ActiveBadge({ isActive }: { isActive: boolean | null }) {
  if (isActive === true) {
    return (
      <span className="ml-1 inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded bg-green-100 text-green-800">
        Active
      </span>
    );
  }
  if (isActive === false) {
    return (
      <span className="ml-1 inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-200 text-gray-700">
        Inactive
      </span>
    );
  }
  return null;
}

function MemberRow({ user }: { user: TeamUserSubscriptionItem }) {
  return (
    <tr className="border-t border-gray-100 align-top">
      <td className="px-2 py-1.5 font-medium text-gray-900 whitespace-nowrap">
        {user.username}
        <ActiveBadge isActive={user.is_active} />
      </td>
      <td className="px-2 py-1.5 text-gray-600 max-w-[160px] truncate" title={user.email || undefined}>
        {user.email?.trim() ? user.email : '—'}
      </td>
      <td className="px-2 py-1.5 text-gray-700 whitespace-nowrap text-center">
        {user.seat_no != null ? user.seat_no : '—'}
      </td>
      <td className="px-2 py-1.5 text-gray-600 whitespace-nowrap">{formatLastLogin(user.last_login)}</td>
      <td className="px-2 py-1.5 text-gray-700 whitespace-nowrap text-right">{user.login_count ?? 0}</td>
    </tr>
  );
}

function TooltipBody({ state }: { state: TeamSeatMembersState }) {
  if (state.status === 'idle' || state.status === 'loading') {
    return (
      <div className="flex items-center gap-2 px-3 py-4 text-sm text-gray-600">
        <Spinner size="sm" />
        <span>Loading seat members…</span>
      </div>
    );
  }

  if (state.status === 'error') {
    return <div className="px-3 py-4 text-sm text-red-600">{state.error}</div>;
  }

  const { users, pagination } = state.data;
  if (!users.length) {
    return <div className="px-3 py-4 text-sm text-gray-500">No seated users on this team.</div>;
  }

  return (
    <div className="max-h-72 overflow-auto">
      <table className="min-w-full text-xs">
        <thead className="bg-gray-50 sticky top-0">
          <tr>
            <th className="px-2 py-1.5 text-left font-medium text-gray-500">Username</th>
            <th className="px-2 py-1.5 text-left font-medium text-gray-500">Email</th>
            <th className="px-2 py-1.5 text-center font-medium text-gray-500">Seat #</th>
            <th className="px-2 py-1.5 text-left font-medium text-gray-500">Last login</th>
            <th className="px-2 py-1.5 text-right font-medium text-gray-500">Logins</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <MemberRow key={user.user_id} user={user} />
          ))}
        </tbody>
      </table>
      {pagination?.has_more && (
        <p className="px-2 py-1.5 text-[11px] text-gray-500 border-t border-gray-100">
          Showing first {users.length} of {pagination.total} seated users.
        </p>
      )}
    </div>
  );
}

/**
 * Fetches seated team members on first hover and shows a portal tooltip.
 * Disabled triggers render children with no hover affordance / no fetch.
 */
const SeatMembersHover: React.FC<SeatMembersHoverProps> = ({
  teamId,
  enabled = true,
  children,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<TeamSeatMembersState>(() => peekTeamSeatMembers(teamId));
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const tooltipId = useId();
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const updatePosition = () => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const preferredTop = rect.bottom + 6;
    const preferredLeft = rect.left;
    const maxLeft = window.innerWidth - 420;
    setCoords({
      top: preferredTop,
      left: Math.max(8, Math.min(preferredLeft, maxLeft)),
    });
  };

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const handleEnter = () => {
    if (!enabled) return;
    clearCloseTimer();
    setOpen(true);
    updatePosition();

    const cached = peekTeamSeatMembers(teamId);
    if (cached.status === 'success') {
      setState(cached);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setState({ status: 'loading' });
    void loadTeamSeatMembers(teamId, controller.signal).then((next) => {
      if (controller.signal.aborted || next.status === 'error' && next.error === 'aborted') return;
      setState(next);
    });
  };

  const handleLeave = () => {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open, state.status]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => updatePosition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  useEffect(
    () => () => {
      clearCloseTimer();
      abortRef.current?.abort();
    },
    []
  );
  if (!enabled) {
    return <span className={className}>{children}</span>;
  }

  return (
    <>
      <span
        ref={triggerRef}
        className={`inline-flex cursor-help border-b border-dotted border-gray-400 ${className}`.trim()}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onFocus={handleEnter}
        onBlur={handleLeave}
        tabIndex={0}
        aria-describedby={open ? tooltipId : undefined}
      >
        {children}
      </span>
      {open &&
        coords &&
        createPortal(
          <div
            id={tooltipId}
            role="tooltip"
            className="fixed z-[100] w-[400px] rounded-md border border-gray-200 bg-white shadow-lg"
            style={{ top: coords.top, left: coords.left }}
            onMouseEnter={clearCloseTimer}
            onMouseLeave={handleLeave}
          >
            <div className="px-3 py-2 border-b border-gray-100 text-xs font-semibold text-gray-700">
              Team seat members
            </div>
            <TooltipBody state={state} />
          </div>,
          document.body
        )}
    </>
  );
};

export default SeatMembersHover;
